// ---------------------------------------------------------------------------
// Submission feedback — Option A (advisory precheck) + Option B (async polling)
//
// Surfaces downstream submission errors back to the logged-in user.
//   • Option A: an ADVISORY duplicate-ID precheck before the submit. It NEVER
//     blocks — if the ID is already registered the caller shows an info notice
//     and submits anyway (the Stage-2 duplicate is suppressed for the user, so
//     this upfront notice is the only place they learn of it). Network/5xx/
//     timeout all fail open (treated as not-duplicate).
//   • Option B: after a submission is accepted, poll the mediator for the async
//     outcome (Stage-1 SHR result + Stage-2 CarePay result) keyed by a
//     wrapper-minted requestId, and toast the result if it arrives while the
//     page is open. Slow results fall back to out-of-band notify / "my
//     submissions" (server side, not handled here).
//
// Both call the OpenHIM mediator at window.__APP_CONFIG__.mediatorBaseUrl.
// When that is empty the feature is OFF and submission behaves exactly as before
// (fail-open), so this can ship ahead of the mediator endpoints.
//
// Mediator contract (HIE side — see MamaToto-HIE/plan/):
//   POST {base}/forms/precheck
//        body  { identification, idType? }   (identification number only)
//        200   { status:"ok", duplicate:true, message } | { status:"ok", duplicate:false }
//   GET  {base}/forms/status/{requestId}
//        200   { stage, status: "pending"|"ok"|"error", code?, message?, fixable? }
//        404   no record yet (treated as pending)
// ---------------------------------------------------------------------------

// Cap how long the precheck can hold up a submission. A hung/slow mediator
// aborts and fails open rather than stalling the user (intent: never block
// submissions on infra).
const PRECHECK_TIMEOUT_MS = 5000;

// Mediator routes require a Keycloak bearer token (forwarded by OpenHIM). The
// token is optional here: the web-component embed path has no Keycloak, and a
// missing token simply means no Authorization header (the mediator decides).
const authHeader = (token?: string): Record<string, string> =>
  token ? { Authorization: `Bearer ${token}` } : {};

export interface HiddenFieldValue {
  id: string;
  name: string;
  value: string;
}

const trimBase = (base: string): string => base.replace(/\/+$/, '');

// ---------------------------------------------------------------------------
// Identification + DOB resolution over the already-fetched form definition.
//
// We resolve the identification number and date of birth by matching field
// TITLES (case-insensitive keyword match), not hard-coded field ids, so the
// same logic works across the mom/test and social forms without a per-form map.
// ---------------------------------------------------------------------------

export interface FormFieldLike {
  id: string;
  title?: unknown;
}

/** Plain lower-cased text of a field title (tolerates rich/array titles). */
const titleText = (title: unknown): string => {
  if (typeof title === 'string') return title.toLowerCase();
  if (Array.isArray(title)) return title.map(titleText).join(' ');
  return '';
};

/** First field whose title matches the predicate. */
const findFieldByTitle = (
  fields: FormFieldLike[] | undefined,
  pred: (title: string) => boolean,
): FormFieldLike | undefined => (fields ?? []).find(f => pred(titleText(f.title)));

/** Trimmed SCALAR value (string/number only) for a field id. Composite answers
 *  — choice / yes-no / file → array or object — return '' so they can't be
 *  mistaken for an identification number or String([...])-stringified into garbage. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const scalarValue = (values: Record<string, any>, field?: FormFieldLike): string => {
  if (!field) return '';
  const v = values[field.id];
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return '';
};

export type IdType = 'national-id' | 'passport';

export interface ResolvedIdentification {
  identification: string;
  idType?: IdType;
}

/**
 * First field whose title matches `pred` AND holds a non-empty SCALAR value.
 *
 * Filtering on the scalar value (not the title's shape) is what makes this
 * robust: yes/no and choice fields answer as non-scalars, so scalarValue()
 * returns '' and they're skipped — while a legitimate ID field whose title is
 * phrased as a question ("What is your national ID number?") is still accepted.
 * It also skips a keyword match the user left blank (e.g. an empty passport
 * field when they filled the national ID) and continues to the next candidate.
 *
 * (Replaces the old `!isQuestionTitle` guard, which excluded every "?"-titled
 * field — including real ID fields, since HeyForm titles are questions — and
 * could latch onto the first keyword match even when it was empty/non-scalar.)
 */
const firstScalarByTitle = (
  fields: FormFieldLike[] | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: Record<string, any>,
  pred: (title: string) => boolean,
): string => {
  for (const f of fields ?? []) {
    if (!pred(titleText(f.title))) continue;
    const v = scalarValue(values, f);
    if (v) return v;
  }
  return '';
};

/**
 * Resolve the identification number to precheck: prefer the national-ID field
 * (title contains "national" + "id"); fall back to the passport field (title
 * contains "passport"). Matches the first such field that actually holds a
 * scalar value, so a question-phrased title or an unfilled duplicate-keyword
 * field doesn't defeat resolution. Empty when neither is filled → precheck no-ops.
 */
export function resolveIdentification(
  fields: FormFieldLike[] | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: Record<string, any>,
): ResolvedIdentification {
  const national = firstScalarByTitle(fields, values, t => t.includes('national') && t.includes('id'));
  if (national) return { identification: national, idType: 'national-id' };

  const passport = firstScalarByTitle(fields, values, t => t.includes('passport'));
  if (passport) return { identification: passport, idType: 'passport' };

  return { identification: '' };
}

/**
 * Basic client-side date-of-birth sanity check. FAILS OPEN: returns an error
 * message (blocking the submit) ONLY when a DOB confidently parses to an
 * impossible value — a future year, or before 1900. Anything we can't confidently
 * parse (unknown shape, non-ISO/locale string, NaN) returns null and does NOT
 * block — never trap the user on a format quirk or a mis-matched field; the
 * renderer enforces required-ness and the mediator catches real eligibility.
 *
 * The field is matched tightly (a date-of-birth title, not just any "birth"
 * word) so free-text fields like "Place of birth" can't be picked up.
 */
export function validateDateOfBirth(
  fields: FormFieldLike[] | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: Record<string, any>,
): string | null {
  const field = findFieldByTitle(
    fields,
    t => t === 'dob' || (t.includes('birth') && (t.includes('date') || t.includes('dob'))),
  );
  if (!field) return null;
  const raw = values[field.id];
  if (raw == null || raw === '') return null;

  // HeyForm date answers arrive as a Date, a string ("YYYY-MM-DD"), a number, or
  // a {year,month,day} object. Anything else (array/composite) → fail open.
  let date: Date;
  if (raw instanceof Date) {
    date = raw;
  } else if (typeof raw === 'string' || typeof raw === 'number') {
    date = new Date(raw);
  } else if (typeof raw === 'object' && !Array.isArray(raw) && 'year' in raw) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { year, month, day } = raw as any;
    const y = Number(year), m = Number(month ?? 1), d = Number(day ?? 1);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    date = new Date(y, m - 1, d);
  } else {
    return null; // unknown shape — fail open
  }

  if (Number.isNaN(date.getTime())) return null; // unparseable — fail open, never block

  // Compare by YEAR only: avoids any UTC-vs-local timezone edge near "today".
  const thisYear = new Date().getFullYear();
  if (date.getFullYear() > thisYear) {
    return 'Date of birth cannot be in the future. Please check and correct it.';
  }
  if (date.getFullYear() < 1900) {
    return 'Please enter a valid date of birth.';
  }
  return null;
}

export interface PrecheckResult {
  /** true when the ID is already registered with CarePay. Advisory only. */
  duplicate: boolean;
  /** Human-readable notice from the mediator on a duplicate. */
  message?: string;
}

/**
 * Option A — advisory duplicate-ID precheck.
 *
 * Calls `POST {base}/forms/precheck { identification, idType? }`. ALWAYS returns
 * (never throws) and is NON-BLOCKING: a missing base URL, missing identification,
 * network failure, timeout, 5xx, or any non-`duplicate:true` body all resolve to
 * `{ duplicate: false }`. The only signal it ever surfaces is `duplicate: true`,
 * which the caller treats as an info notice and submits anyway.
 */
export async function precheckSubmission(
  base: string | undefined,
  input: ResolvedIdentification,
  token?: string,
): Promise<PrecheckResult> {
  if (!base || !input.identification) return { duplicate: false };

  const url = `${trimBase(base)}/forms/precheck`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PRECHECK_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader(token) },
      body: JSON.stringify({ identification: input.identification, idType: input.idType }),
      signal: controller.signal,
    });
    if (!res.ok) return { duplicate: false };
    const body = (await res.json()) as { duplicate?: boolean; message?: string };
    return body.duplicate === true
      ? { duplicate: true, message: body.message }
      : { duplicate: false };
  } catch {
    // Network / CORS / timeout / mediator down — advisory, so never block.
    return { duplicate: false };
  } finally {
    clearTimeout(timeout);
  }
}

export interface SubmissionStatus {
  stage?: string;
  status: 'pending' | 'ok' | 'error';
  code?: string;
  message?: string;
  fixable?: boolean;
}

export interface PollOptions {
  onResult: (status: SubmissionStatus) => void;
  /** Called once when polling ends on its own (terminal status, permanent error,
   *  or timeout) — NOT when cancelled externally. Use it to prune tracking. */
  onSettled?: () => void;
  /** Keycloak bearer token for the status request (omitted on the no-auth path). */
  token?: string;
  intervalMs?: number;
  timeoutMs?: number;
}

/**
 * Option B — bounded polling for the async outcome of an accepted submission.
 *
 * Polls `{base}/forms/status/{requestId}` until a terminal status (`ok`/`error`)
 * arrives or `timeoutMs` elapses. Calls `onResult` exactly once on a terminal
 * status; stays silent on timeout (the server-side notify / "my submissions"
 * fallback covers slow Stage-2 results). Returns a cancel function — call it on
 * unmount to stop polling.
 */
export function pollSubmissionStatus(
  base: string | undefined,
  requestId: string,
  { onResult, onSettled, token, intervalMs = 3000, timeoutMs = 60_000 }: PollOptions,
): () => void {
  if (!base) return () => undefined;

  const url = `${trimBase(base)}/forms/status/${encodeURIComponent(requestId)}`;
  const deadline = Date.now() + timeoutMs;
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const tick = async (): Promise<void> => {
    if (cancelled) return;

    try {
      const res = await fetch(url, {
        headers: { Accept: 'application/json', ...authHeader(token) },
      });
      if (res.ok) {
        const status = (await res.json()) as SubmissionStatus;
        if (status.status === 'ok' || status.status === 'error') {
          if (!cancelled) {
            onResult(status);
            onSettled?.();
          }
          return;
        }
        // any other status (e.g. "pending") → keep waiting
      } else if (res.status >= 400 && res.status < 500 && res.status !== 404) {
        // Permanent client error — but 404 means "no record yet", so keep
        // waiting for that. Anything else 4xx will never succeed: stop polling
        // instead of retrying for the full deadline.
        if (!cancelled) onSettled?.();
        return;
      }
      // 404 (pending) or 5xx (transient) → fall through and retry.
    } catch {
      // network error — swallow and retry until the deadline
    }

    if (cancelled) return;
    if (Date.now() >= deadline) {
      onSettled?.();
      return;
    }
    timer = setTimeout(() => void tick(), intervalMs);
  };

  void tick();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
}
